import {
  ActionGroup,
  Button,
  Divider,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  TextInput,
  PageSection,
  Spinner,
  Title,
} from '@patternfly/react-core'
import { CheckIcon, ErrorCircleOIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import type { RootState } from '@app/state'
import * as User from '@app/state/user'

export const AccountSettingsScreen = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const userIdentifier = useSelector<RootState, string>(
    (state) => state.auth.identifier,
  )

  const updatePasswordStatus = useSelector<RootState, string>(
    (state) => state.user.passwordUpdateStatus,
  )

  const [currentPassword, setCurrentPassword] = useState<string>(undefined)
  const [newPassword, setNewPassword] = useState<string>(undefined)
  const [confirmPassword, setConfirmPassword] = useState<string>(undefined)
  const [passwordsMatch, setPasswordsMatch] = useState(true)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)

  const updatePassword = useCallback(
    (identifer: string, password: string, newPassword: string) => {
      dispatch(User.actions.updatePassword(identifer, password, newPassword))
    },
    [dispatch],
  )

  const isBlank = useCallback((value: string) => {
    return value == null || value === ''
  }, [])

  const checkIsFormValid = useCallback(
    (currentPassword: string, newPassword: string, confirmPassword: string) => {
      if (
        isBlank(currentPassword) ||
        isBlank(newPassword) ||
        isBlank(confirmPassword)
      ) {
        return false
      } else if (newPassword !== confirmPassword) {
        return false
      } else {
        return true
      }
    },
    [isBlank],
  )

  const checkPasswordsMatch = useCallback((p1: string, p2: string) => {
    return p1 === p2
  }, [])

  const handleCurrentPasswordChange = useCallback(
    (password: string) => {
      setCurrentPassword(password)
      setIsFormValid(checkIsFormValid(password, newPassword, confirmPassword))
    },
    [checkIsFormValid, confirmPassword, newPassword],
  )

  const handleNewPasswordChange = useCallback(
    (password: string) => {
      setNewPassword(password)
      setPasswordsMatch(checkPasswordsMatch(password, confirmPassword))
      setIsFormValid(
        checkIsFormValid(currentPassword, password, confirmPassword),
      )
    },
    [checkIsFormValid, checkPasswordsMatch, confirmPassword, currentPassword],
  )

  const handleConfirmPasswordChange = useCallback(
    (password: string) => {
      setConfirmPassword(password)
      setPasswordsMatch(checkPasswordsMatch(password, newPassword))
      setIsFormValid(checkIsFormValid(currentPassword, newPassword, password))
    },
    [checkIsFormValid, checkPasswordsMatch, currentPassword, newPassword],
  )

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      updatePassword(userIdentifier, currentPassword, newPassword)
      setIsFormSubmitted(true)
    },
    [currentPassword, newPassword, updatePassword, userIdentifier],
  )

  const renderStatus = useCallback(() => {
    if (updatePasswordStatus === User.UserActionStatus.Pending) {
      return (
        <div>
          <Spinner size="md" />
        </div>
      )
    } else if (updatePasswordStatus === User.UserActionStatus.Succeeded) {
      return (
        <div>
          <CheckIcon size="md" color="green" /> Password updated successfully!
        </div>
      )
    } else if (updatePasswordStatus === User.UserActionStatus.Failed) {
      return (
        <div>
          <ErrorCircleOIcon size="md" color="red" /> Current password incorrect.
          Please try again.
        </div>
      )
    } else {
      return <div />
    }
  }, [updatePasswordStatus])

  return (
    <PageSection>
      <Title headingLevel="h1" size="xl">
        Account Settings
      </Title>
      <Flex>
        <FlexItem>
          <div className="ci-c-section-basic">
            <div className="ci-c-form-basic">
              <Form onSubmit={handleSubmit}>
                <Title headingLevel="h2" size="lg">
                  Change Password
                </Title>
                <Divider />
                <FormGroup
                  label="Current Password"
                  isRequired
                  fieldId="current-password"
                >
                  <TextInput
                    isRequired
                    type="password"
                    id="current-password"
                    name="current-password"
                    value={currentPassword}
                    onChange={handleCurrentPasswordChange}
                  />
                </FormGroup>
                <FormGroup
                  label="New Password"
                  isRequired
                  fieldId="new-password"
                >
                  <TextInput
                    isRequired
                    type="password"
                    id="new-password"
                    name="new-password"
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                  />
                </FormGroup>
                <FormGroup
                  label="Confirm New Password"
                  isRequired
                  fieldId="confirm-password"
                  validated={passwordsMatch ? 'success' : 'error'}
                  helperTextInvalid="Passwords must match"
                >
                  <TextInput
                    isRequired
                    type="password"
                    id="confirm-password"
                    name="confirm-password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                  />
                </FormGroup>
                <ActionGroup style={{ float: 'right', marginTop: 10 }}>
                  <Button
                    style={{ marginRight: 5 }}
                    variant="secondary"
                    onClick={() => history.go(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isDisabled={!isFormValid}
                    onClick={handleSubmit}
                  >
                    Change Password
                  </Button>
                </ActionGroup>
                {isFormSubmitted ? renderStatus() : null}
              </Form>
            </div>
          </div>
        </FlexItem>
      </Flex>
    </PageSection>
  )
}
